import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "../api/client.js";
import type { MembershipSummary, SafeUser, WorkspaceSummary } from "../api/types.js";

const SELECTED_TENANT_STORAGE_KEY = "emma_selected_tenant_id";

interface AuthState {
  readonly status: "loading" | "authenticated" | "unauthenticated";
  readonly user: SafeUser | null;
  readonly memberships: readonly MembershipSummary[];
  readonly accessibleTenants: readonly string[];
  readonly workspaces: readonly WorkspaceSummary[];
  readonly videoManagementTenantId: string | null;
  readonly csrfToken: string | null;
  readonly selectedTenantId: string | null;
}

interface AuthContextValue extends AuthState {
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  loginError: string | null;
  selectTenant(tenantId: string): void;
  refresh(): Promise<void>;
}

/** Per-viewer convenience only (see the artifact/browser-storage doctrine this codebase's own conventions follow) — never authoritative, always re-validated against the real `workspaces`/`videoManagementTenantId` list on every `/auth/me` refresh. */
function readStoredTenantId(): string | null {
  try {
    return localStorage.getItem(SELECTED_TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredTenantId(tenantId: string): void {
  try {
    localStorage.setItem(SELECTED_TENANT_STORAGE_KEY, tenantId);
  } catch {
    // Best-effort only — a private/blocked storage context simply means the selection resets on next load.
  }
}

/**
 * P1 fix (GENERATE backlog remediation): pure, directly unit-testable
 * resolution of `selectedTenantId` — extracted out of `refresh()` below so
 * the OWNER-vs-non-owner branching has a dedicated regression test with no
 * React rendering involved. The video-management tenant (Emma's own,
 * internal) is a valid SELECTION only for a platform OWNER — see
 * `useCurrentTenant`'s own doc history for why OWNER needs it at all.
 * Previously the caller's `validSelections`/fallback both included
 * `videoManagementTenantId` unconditionally, so a NORMAL (non-owner) user
 * could end up with Emma's tenant as their selected/default workspace (via
 * a stale stored selection, or simply because they have zero workspaces of
 * their own) and could then issue GENERATE calls against Emma's own
 * tenant. A non-owner's candidate set is their own workspace memberships
 * ONLY, both for what counts as a valid stored selection and for the
 * no-valid-stored-selection fallback.
 */
export function resolveSelectedTenantId(input: {
  readonly isPlatformOwner: boolean;
  readonly workspaces: readonly { readonly tenantId: string }[];
  readonly videoManagementTenantId: string | null;
  readonly storedTenantId: string | null;
}): string | null {
  const validSelections = new Set<string>([
    ...input.workspaces.map((w) => w.tenantId),
    ...(input.isPlatformOwner && input.videoManagementTenantId !== null ? [input.videoManagementTenantId] : []),
  ]);
  const storedIsValid = input.storedTenantId !== null && validSelections.has(input.storedTenantId);
  if (storedIsValid) {
    return input.storedTenantId;
  }
  return input.isPlatformOwner ? (input.videoManagementTenantId ?? input.workspaces[0]?.tenantId ?? null) : (input.workspaces[0]?.tenantId ?? null);
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Emma OS GENERATE productization: `csrfToken` is held ONLY in React
 * state — never written to localStorage/sessionStorage (Section 4 of
 * the product spec: no secret in browser storage). This is safe because
 * it is reissued on every successful /auth/login or /auth/me call; a
 * page refresh simply re-fetches /auth/me (using the HttpOnly session
 * cookie the browser already sends automatically) and gets a fresh
 * token back — the exact same durable-backend-state-is-authoritative
 * doctrine Section 1 of the product spec requires for the whole app.
 */
const EMPTY_STATE: AuthState = { status: "loading", user: null, memberships: [], accessibleTenants: [], workspaces: [], videoManagementTenantId: null, csrfToken: null, selectedTenantId: null };
const SIGNED_OUT_STATE: AuthState = { ...EMPTY_STATE, status: "unauthenticated" };

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<AuthState>(EMPTY_STATE);
  const [loginError, setLoginError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.get<{
        user: SafeUser;
        memberships: MembershipSummary[];
        accessibleTenants: string[];
        workspaces: WorkspaceSummary[];
        videoManagementTenantId: string | null;
        csrfToken: string;
      }>("/auth/me");
      const selectedTenantId = resolveSelectedTenantId({
        isPlatformOwner: result.user.isPlatformOwner,
        workspaces: result.workspaces,
        videoManagementTenantId: result.videoManagementTenantId,
        storedTenantId: readStoredTenantId(),
      });
      setState({
        status: "authenticated",
        user: result.user,
        memberships: result.memberships,
        accessibleTenants: result.accessibleTenants,
        workspaces: result.workspaces,
        videoManagementTenantId: result.videoManagementTenantId,
        csrfToken: result.csrfToken,
        selectedTenantId,
      });
    } catch {
      setState(SIGNED_OUT_STATE);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    setLoginError(null);
    try {
      await api.post<{ user: SafeUser; csrfToken: string }>("/auth/login", null, { email, password });
      await refresh();
    } catch (error) {
      setLoginError(error instanceof ApiError ? "Invalid email or password." : "Login failed — please try again.");
      throw error;
    }
  }, [refresh]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout", state.csrfToken).catch(() => undefined);
    setState(SIGNED_OUT_STATE);
  }, [state.csrfToken]);

  const selectTenant = useCallback((tenantId: string) => {
    writeStoredTenantId(tenantId);
    setState((prev) => ({ ...prev, selectedTenantId: tenantId }));
  }, []);

  return <AuthContext.Provider value={{ ...state, login, logout, loginError, selectTenant, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
